import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { authenticatedFetch } from '../services/authService';
import { useAuthenticatedEffect } from '../hooks/useAuthenticatedEffect';
import { useMapyGoogle } from './LadowanieMapGoogle';
import GranicaBleduMapy from './GranicaBleduMapy';
import { useAuth } from '../contexts/AuthContext';
import { useStatusColors } from '../contexts/StatusColorsContext';
import {
  budowyDnia, stanZamowienia, FILTR_DOMYSLNY,
  type BudowaNaMapie, type FiltrStanow, type FiltrStanowTabeli, type StanFiltrowany, type StanZamowienia, type ZamowienieNaMapie
} from '../utils/budowyNaMapie';

interface MapWidgetProps {
  selectedRegion: number | null;
  hideHeader?: boolean;
  externalLoading?: boolean;
  onReady?: () => void;
  // Zamowienia otwartego dnia (lista z tabeli). Bez niej karta pokazuje same wezly, bez kropek.
  zamowienia?: ZamowienieNaMapie[];
  // Filtr stanow tabeli: kropki przyjmuja jego wartosci przy kazdej zmianie w tabeli.
  filtrTabeli?: FiltrStanowTabeli | null;
}

interface WezelItem {
  id: number;
  nazwa: string;
  region_id: number;
  latitude?: number | null;
  longitude?: number | null;
  lat?: number | null;
  lon?: number | null;
}

const WARSAW_COORDS = { lat: 52.2297, lon: 21.0122 };
const WEZEL_ICON_PX = 36;

// Przelaczniki-kropki na mapie: podpis tylko w podpowiedzi, zeby nie zabieraly miejsca.
const KROPKI_STANOW: Array<{ stan: StanFiltrowany; etykieta: string }> = [
  { stan: 'Potwierdzone', etykieta: 'Potwierdzone' },
  { stan: 'Realizacja', etykieta: 'Realizacja' },
  { stan: 'Zakonczone', etykieta: 'Zakończone' },
  { stan: 'Anulowane', etykieta: 'Anulowane' },
];
const STAN_W_DYMKU: Record<StanZamowienia, string> = {
  Nowe: 'nowe', Potwierdzone: 'potwierdzone', Realizacja: 'w realizacji', Zakonczone: 'zakończone', Anulowane: 'anulowane'
};
// Zamowienie bez zadnej flagi nie ma koloru w tabeli; na mapie neutralny szaroniebieski.
const KOLOR_NOWEGO = '#90a4ae';
const KOLOR_WEZLOW = '#c62828';
const KLUCZ_FILTRA = 'zamowienia.mapa.filtr';

type FiltrMapy = FiltrStanow & { Wezly: boolean };
const FILTR_MAPY_DOMYSLNY: FiltrMapy = { ...FILTR_DOMYSLNY, Wezly: true };

function wczytajFiltrMapy(): FiltrMapy {
  const filtr = { ...FILTR_MAPY_DOMYSLNY };
  try {
    const zapisany = JSON.parse(localStorage.getItem(KLUCZ_FILTRA) || 'null');
    for (const k of Object.keys(filtr) as Array<keyof FiltrMapy>) {
      if (typeof zapisany?.[k] === 'boolean') filtr[k] = zapisany[k];
    }
  } catch {}
  return filtr;
}

// 1 zamowienie, 2-4 zamowienia (bez 12-14), 5+ zamowien.
function odmiana(n: number, jeden: string, kilka: string, wiele: string): string {
  if (n === 1) return jeden;
  const r10 = n % 10;
  const r100 = n % 100;
  return r10 >= 2 && r10 <= 4 && (r100 < 12 || r100 > 14) ? kilka : wiele;
}

const stylKropki = (kolor: string, wlaczona: boolean, kwadrat: boolean): React.CSSProperties => ({
  width: 12,
  height: 12,
  padding: 0,
  boxSizing: 'border-box',
  cursor: 'pointer',
  borderRadius: kwadrat ? 2 : '50%',
  border: `2px solid ${kolor}`,
  background: wlaczona ? kolor : 'transparent',
  opacity: wlaczona ? 1 : 0.55
});

// Dymek budowy wylacznie przez textContent: nazwy klientow i budow pochodza z zamowien.
function trescDymku(b: BudowaNaMapie): HTMLElement {
  const dymek = document.createElement('div');
  dymek.style.cssText = 'font: 12px/1.45 sans-serif; color: #263238; max-width: 280px';
  const tytul = document.createElement('div');
  tytul.style.fontWeight = '700';
  tytul.textContent = b.nazwa;
  dymek.appendChild(tytul);
  for (const z of b.zamowienia) {
    const godziny = [z.zamowienie_godzinaod, z.zamowienie_godzinado].map((g) => String(g || '').slice(0, 5)).filter(Boolean).join('–');
    const ilosc = z.zamowienie_ilosc != null && Number.isFinite(Number(z.zamowienie_ilosc))
      ? `${Number(z.zamowienie_ilosc).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} m³`
      : '';
    const wiersz = document.createElement('div');
    wiersz.textContent = [godziny, ilosc, z.wezel_nazwa || '', STAN_W_DYMKU[stanZamowienia(z)]].filter(Boolean).join(' · ');
    dymek.appendChild(wiersz);
  }
  return dymek;
}

// Poza MapWidget celowo: zdefiniowany w jego ciele byl przy kazdym renderze nowym typem komponentu,
// wiec React odmontowywal mape i tworzyl ja od nowa (kazda nowa google.maps.Map to kolejne platne
// ladowanie mapy), a onLoad -> setMapReadyTick -> render zamykal petle bez konca.
const MapCanvas: React.FC<{
  apiKey: string;
  mapId: string;
  coords: { lat: number; lon: number };
  markers: Array<{ lat: number; lon: number }>;
  onLoad: (map: any) => void;
  fitBoundsFn: (map: any, markers: Array<{ lat: number; lon: number }>, centerCoords: { lat: number; lon: number }) => void;
  onReady?: () => void;
}> = ({ apiKey, mapId, coords, markers, onLoad, fitBoundsFn, onReady }) => {
  const { isLoaded, loadError } = useMapyGoogle(apiKey);

  // Stale obiekty: GoogleMap przy kazdej nowej referencji center/options wola setCenter/setOptions,
  // czyli kazdy render karty przesuwalby mape z powrotem na srodek.
  const center = useMemo(() => ({ lat: coords.lat, lng: coords.lon }), [coords.lat, coords.lon]);
  const options = useMemo(() => ({
    streetViewControl: false,
    mapTypeControl: false,
    mapId: mapId.trim()
  }), [mapId]);

  useEffect(() => {
    if (loadError) {
      onReady?.();
    }
  }, [loadError, onReady]);

  if (loadError) {
    return (
      <div style={{ fontSize: '11px', color: '#d32f2f', padding: '8px' }}>
        Błąd ładowania Google Maps
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={{ fontSize: '11px', color: '#757575', padding: '8px' }}>
        Ładowanie Google Maps...
      </div>
    );
  }

  return (
    <GoogleMap
      onLoad={(map) => {
        onLoad(map);
        fitBoundsFn(map, markers, coords);
        onReady?.();
      }}
      onUnmount={() => {}}
      mapContainerStyle={{ height: '100%', width: '100%', minHeight: '200px' }}
      center={center}
      options={options}
    >
      {/* Advanced markers managed imperatively */}
    </GoogleMap>
  );
};

const MapWidget: React.FC<MapWidgetProps> = ({
  selectedRegion,
  hideHeader = false,
  externalLoading = false,
  onReady,
  zamowienia,
  filtrTabeli
}) => {
  const statusColors = useStatusColors();
  const [filtr, setFiltr] = useState<FiltrMapy>(wczytajFiltrMapy);
  const [pokazBezLokalizacji, setPokazBezLokalizacji] = useState(false);
  // Filtr ustawiony w tabeli zamowien przechodzi na kropki (przy starcie i po kazdej zmianie).
  // Kropki mozna potem przelaczac na samej mapie - do nastepnej zmiany filtra w tabeli.
  useEffect(() => {
    if (!filtrTabeli) return;
    setFiltr((f) => ({
      ...f,
      Potwierdzone: filtrTabeli.potwierdzone !== false,
      Realizacja: filtrTabeli.realizacja !== false,
      Zakonczone: filtrTabeli.zakonczone !== false,
      Anulowane: filtrTabeli.anulowane !== false
    }));
  }, [filtrTabeli]);
  const dzien = useMemo(() => budowyDnia(zamowienia ?? [], filtr), [zamowienia, filtr]);
  const przelacz = (klucz: keyof FiltrMapy) => setFiltr((f) => {
    const nowy = { ...f, [klucz]: !f[klucz] };
    try { localStorage.setItem(KLUCZ_FILTRA, JSON.stringify(nowy)); } catch {}
    return nowy;
  });
  // Kolory stanow z ustawien, te same co wiersze tabeli (mocniejszy odcien „text”).
  const kolorStanu = useCallback((stan: StanZamowienia): string => (
    stan === 'Nowe' ? KOLOR_NOWEGO : (statusColors as any)?.[stan]?.text || KOLOR_NOWEGO
  ), [statusColors]);

  const [wezels, setWezels] = useState<WezelItem[]>([]);
  const [wezelMarkers, setWezelMarkers] = useState<Array<{ id: number; nazwa: string; lat: number; lon: number }>>([]);
  const [coords, setCoords] = useState<{ lat: number; lon: number }>(WARSAW_COORDS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mapRef = useRef<any | null>(null);
  const advWezelMarkersRef = useRef<any[]>([]);
  const [mapReadyTick, setMapReadyTick] = useState(0);

  // Read API key
  const readApiKey = (): string => {
    try {
      const w: any = (typeof window !== 'undefined') ? (window as any) : undefined;
      const fromGlobal = w?.__GMAPS_LOADER_KEY__;
      const meta = (typeof document !== 'undefined') ? (document.querySelector('meta[name="gmaps-api-key"]') as HTMLMetaElement | null) : null;
      const fromWindow = w?.GMAPS_API_KEY;
      const fromMeta = meta?.content;
      const fromStorage = (typeof window !== 'undefined') ? localStorage.getItem('GMAPS_API_KEY') : null;
      const k = (fromGlobal || fromWindow || fromMeta || fromStorage || '').toString();
      return k;
    } catch {
      return '';
    }
  };

  // apiKey as state - fetched asynchronously if not available
  const [apiKey, setApiKey] = useState<string>(() => readApiKey());
  const [apiKeyLoading, setApiKeyLoading] = useState<boolean>(() => !readApiKey());

  // Zapytania tej karty czekaja na token z portalu (useAuthenticatedEffect): wyslane od razu przy
  // starcie dostawaly 401 i nie byly ponawiane, stad „Brak Map ID” na produkcji.
  useAuthenticatedEffect(() => {
    const current = readApiKey();
    console.log('[MapWidget] Initial apiKey from meta/window:', current ? '(present)' : '(empty)');
    if (current) {
      setApiKey(current);
      setApiKeyLoading(false);
      return;
    }
    // Fetch from API if not available
    setApiKeyLoading(true);
    let cancelled = false;
    (async () => {
      try {
        console.log('[MapWidget] Fetching apiKey from /api/geocode/key...');
        const res = await authenticatedFetch('/api/geocode/key', { credentials: 'include' });
        if (cancelled) return;
        console.log('[MapWidget] /api/geocode/key response status:', res.status);
        if (res.ok) {
          const j = await res.json();
          console.log('[MapWidget] /api/geocode/key response body:', j?.key ? '(key present)' : '(no key)');
          const key = j && typeof j.key === 'string' ? j.key : '';
          if (!key) {
            console.warn('[MapWidget] apiKey is empty in response');
            setApiKeyLoading(false);
            return;
          }
          console.log('[MapWidget] Setting apiKey');
          // Store globally for other components
          try { (window as any).GMAPS_API_KEY = key; } catch {}
          try { localStorage.setItem('GMAPS_API_KEY', key); } catch {}
          try {
            const meta = (document.querySelector('meta[name="gmaps-api-key"]') as HTMLMetaElement | null);
            if (!meta && typeof document !== 'undefined') {
              const newMeta = document.createElement('meta');
              newMeta.setAttribute('name', 'gmaps-api-key');
              newMeta.setAttribute('content', key);
              document.head.appendChild(newMeta);
            } else if (meta) {
              meta.setAttribute('content', key);
            }
          } catch {}
          setApiKey(key);
        } else {
          console.error('[MapWidget] Failed to fetch apiKey, status:', res.status);
        }
      } catch (e) {
        console.error('[MapWidget] Error fetching apiKey:', e);
      } finally {
        if (!cancelled) {
          setApiKeyLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Read Map ID from meta/window
  const readMapId = (): string => {
    try {
      const m = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null)?.content || (window as any)?.GMAPS_MAP_ID || '';
      return String(m || '');
    } catch { return ''; }
  };

  // mapId as state - fetched asynchronously if not available from meta/window
  const [mapId, setMapId] = useState<string>(() => readMapId());
  const [mapIdLoading, setMapIdLoading] = useState<boolean>(() => !readMapId());

  useAuthenticatedEffect(() => {
    const current = readMapId();
    console.log('[MapWidget] Initial mapId from meta/window:', current || '(empty)');
    if (current) {
      setMapId(current);
      setMapIdLoading(false);
      return;
    }
    // Fetch from API if not available
    setMapIdLoading(true);
    let cancelled = false;
    (async () => {
      try {
        console.log('[MapWidget] Fetching mapId from /api/geocode/map-id...');
        const res = await authenticatedFetch('/api/geocode/map-id', { credentials: 'include' });
        if (cancelled) return;
        console.log('[MapWidget] /api/geocode/map-id response status:', res.status);
        if (res.ok) {
          const j = await res.json();
          console.log('[MapWidget] /api/geocode/map-id response body:', j);
          const id = j && typeof j.mapId === 'string' ? j.mapId : '';
          if (!id) {
            console.warn('[MapWidget] mapId is empty in response');
            setMapIdLoading(false);
            return;
          }
          console.log('[MapWidget] Setting mapId:', id.slice(0, 10) + '...');
          // Store globally for other components
          try { (window as any).GMAPS_MAP_ID = id; } catch {}
          try {
            const meta = (document.querySelector('meta[name="gmaps-map-id"]') as HTMLMetaElement | null);
            if (!meta && typeof document !== 'undefined') {
              const newMeta = document.createElement('meta');
              newMeta.setAttribute('name', 'gmaps-map-id');
              newMeta.setAttribute('content', id);
              document.head.appendChild(newMeta);
            } else if (meta) {
              meta.setAttribute('content', id);
            }
          } catch {}
          setMapId(id);
        } else {
          console.error('[MapWidget] Failed to fetch mapId, status:', res.status);
        }
      } catch (e) {
        console.error('[MapWidget] Error fetching mapId:', e);
      } finally {
        if (!cancelled) {
          setMapIdLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const readyNotifiedRef = useRef(false);
  const notifyReady = useCallback(() => {
    if (readyNotifiedRef.current) return;
    readyNotifiedRef.current = true;
    onReady?.();
  }, [onReady]);

  // Bez zalogowania zapytania tej karty nigdy nie wystartuja (useAuthenticatedEffect), wiec zamiast
  // wiecznego „Ladowanie konfiguracji mapy...” mowimy to wprost i zwalniamy nakladke strony.
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const bezLogowania = !authLoading && !isAuthenticated;
  useEffect(() => {
    if (bezLogowania) notifyReady();
  }, [bezLogowania, notifyReady]);

  // Load wezels for selected region
  useAuthenticatedEffect(() => {
    let aborted = false;
    readyNotifiedRef.current = false;

    const loadWezels = async () => {
      if (!selectedRegion) {
        setWezels([]);
        setWezelMarkers([]);
        setCoords(WARSAW_COORDS);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const resp = await authenticatedFetch(`/api/wezels?region_id=${selectedRegion}`, { credentials: 'include' });
        if (!resp.ok) {
          throw new Error('Failed to load wezels');
        }
        const data = await resp.json();
        const wezelsList: WezelItem[] = data.wezels || [];
        
        if (aborted) return;
        setWezels(wezelsList);

        // Build markers for wezels with valid coordinates
        const markers: Array<{ id: number; nazwa: string; lat: number; lon: number }> = [];
        for (const w of wezelsList) {
          const rawLat = (w.latitude ?? w.lat) as any;
          const rawLon = (w.longitude ?? w.lon) as any;
          const lat = rawLat !== undefined && rawLat !== null ? parseFloat(String(rawLat)) : NaN;
          const lon = rawLon !== undefined && rawLon !== null ? parseFloat(String(rawLon)) : NaN;
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            markers.push({ id: w.id, nazwa: w.nazwa, lat, lon });
          }
        }

        if (aborted) return;
        setWezelMarkers(markers);

        // Set center to first wezel or Warsaw
        if (markers.length > 0) {
          setCoords({ lat: markers[0].lat, lon: markers[0].lon });
        } else {
          setCoords(WARSAW_COORDS);
        }
      } catch (e: any) {
        if (!aborted) {
          setError(e?.message || 'Błąd pobierania węzłów');
          notifyReady();
        }
      } finally {
        if (!aborted) {
          setLoading(false);
        }
      }
    };

    loadWezels();

    return () => { aborted = true; };
  }, [selectedRegion]);

  useEffect(() => {
    if (!loading && !apiKeyLoading && !mapIdLoading && (error || !apiKey || !apiKey.trim() || !mapId || !mapId.trim())) {
      notifyReady();
    }
  }, [loading, apiKeyLoading, mapIdLoading, error, apiKey, mapId, notifyReady]);

  useEffect(() => {
    if (!loading && !error && mapRef.current) {
      notifyReady();
    }
  }, [loading, error, notifyReady]);

  // Fit bounds to show all markers
  const fitBounds = (map: any, markers: { lat: number; lon: number }[], centerCoords: { lat: number; lon: number }) => {
    const g = (typeof window !== 'undefined' ? (window as any).google : undefined);
    if (!map || !g?.maps) return;
    if (markers && markers.length > 0) {
      const b = new g.maps.LatLngBounds();
      markers.forEach(m => b.extend(new g.maps.LatLng(m.lat, m.lon)));
      map.fitBounds(b);
    } else {
      // Default view around center
      const radiusKm = 4;
      const dLat = radiusKm / 111;
      const dLon = radiusKm / (111 * Math.cos((centerCoords.lat * Math.PI) / 180) || 1e-6);
      const sw = new g.maps.LatLng(centerCoords.lat - dLat, centerCoords.lon - dLon);
      const ne = new g.maps.LatLng(centerCoords.lat + dLat, centerCoords.lon + dLon);
      const bounds = new g.maps.LatLngBounds(sw, ne);
      map.fitBounds(bounds);
    }
  };

  // Create AdvancedMarkerElement markers - REQUIRED, no fallback
  useEffect(() => {
    const g: any = (typeof window !== 'undefined') ? (window as any).google : undefined;
    const map = mapRef.current;
    if (!g || !map) return;

    // Validate requirements
    const markerLib = g?.maps?.marker;
    const hasValidMapId = typeof mapId === 'string' && mapId.trim().length > 0;
    const hasAdvancedMarker = !!(markerLib && markerLib.AdvancedMarkerElement);

    if (!hasValidMapId) {
      setError('Brak Map ID – wymagane dla AdvancedMarkerElement');
      return;
    }

    if (!hasAdvancedMarker) {
      setError('AdvancedMarkerElement nie jest dostępny – sprawdź konfigurację Google Maps API');
      return;
    }

    // Clear existing markers
    try {
      advWezelMarkersRef.current.forEach(m => {
        try { m.map = null; } catch {}
      });
      advWezelMarkersRef.current = [];
    } catch {}

    // Create AdvancedMarkerElement markers for wezels (kropka „Węzły” je chowa)
    for (const m of filtr.Wezly ? wezelMarkers : []) {
      try {
        const img = document.createElement('img');
        img.src = '/red_pin_point.png';
        img.style.width = `${WEZEL_ICON_PX}px`;
        img.style.height = `${WEZEL_ICON_PX}px`;
        img.style.transform = 'translate(-50%, -100%)';
        const adv = new markerLib.AdvancedMarkerElement({
          map,
          position: { lat: m.lat, lng: m.lon },
          title: m.nazwa,
          content: img
        });
        advWezelMarkersRef.current.push(adv);
      } catch (err) {
        console.error('[MapWidget] Error creating AdvancedMarkerElement:', err);
      }
    }

    return () => {
      try {
        advWezelMarkersRef.current.forEach(m => {
          try { m.map = null; } catch {}
        });
        advWezelMarkersRef.current = [];
      } catch {}
    };
  }, [wezelMarkers, mapReadyTick, mapId, filtr.Wezly]);

  // Budowy dnia: jeden znacznik na miejsce, kolor stanu, liczba zamowien w pinezce, dymek po kliknieciu.
  const dymekRef = useRef<any | null>(null);
  useEffect(() => {
    const g: any = (typeof window !== 'undefined') ? (window as any).google : undefined;
    const map = mapRef.current;
    const markerLib = g?.maps?.marker;
    if (!map || !markerLib?.AdvancedMarkerElement || !markerLib?.PinElement) return;

    const utworzone: any[] = [];
    for (const b of dzien.budowy) {
      try {
        const pinezka = new markerLib.PinElement({
          background: kolorStanu(b.stan),
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          glyph: b.zamowienia.length > 1 ? String(b.zamowienia.length) : '',
          scale: 0.85
        });
        const znacznik = new markerLib.AdvancedMarkerElement({
          map,
          position: { lat: b.lat, lng: b.lng },
          title: b.nazwa,
          content: pinezka.element,
          zIndex: b.stan === 'Realizacja' ? 30 : 20
        });
        znacznik.addListener('click', () => {
          dymekRef.current = dymekRef.current || new g.maps.InfoWindow();
          dymekRef.current.setContent(trescDymku(b));
          dymekRef.current.open({ map, anchor: znacznik });
        });
        utworzone.push(znacznik);
      } catch (err) {
        console.error('[MapWidget] Błąd znacznika budowy:', err);
      }
    }

    return () => {
      for (const m of utworzone) {
        try { m.map = null; } catch {}
      }
      try { dymekRef.current?.close(); } catch {}
    };
  }, [dzien.budowy, mapReadyTick, kolorStanu]);

  // Kadr: wezly i wszystkie budowy dnia z lokalizacja, bez filtra, wiec klikanie kropek nie
  // przesuwa mapy. Liczony od nowa tylko, gdy zmienia sie zestaw punktow (nie przy zmianie stanu).
  const punktyKadru = useMemo(() => [
    ...wezelMarkers.map((m) => ({ lat: m.lat, lon: m.lon })),
    ...dzien.punktyWszystkich.map((p) => ({ lat: p.lat, lon: p.lng }))
  ], [wezelMarkers, dzien.punktyWszystkich]);
  const kluczKadru = punktyKadru.map((p) => `${p.lat.toFixed(4)},${p.lon.toFixed(4)}`).sort().join('|');
  useEffect(() => {
    const map = mapRef.current;
    if (!map || punktyKadru.length === 0) return;
    fitBounds(map, punktyKadru, coords);
    // Tylko klucz kadru i nowa mapa: punktyKadru/coords zmieniaja tozsamosc przy kazdej zmianie listy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kluczKadru, mapReadyTick]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0px',
        border: hideHeader ? 'none' : '1px solid var(--border)',
        borderRadius: hideHeader ? '0' : '4px',
        background: 'var(--card)',
        minWidth: 0,
        overflow: 'hidden',
        minHeight: hideHeader ? '160px' : 'auto',
        height: hideHeader ? '160px' : 'auto'
      }}
    >
      {!hideHeader && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '6px 10px',
            borderBottom: '1px solid var(--border)',
            background: 'var(--bg)'
          }}
        >
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text)' }}>Mapa</span>
          <span style={{ fontSize: '10px', color: 'var(--muted)' }}>
            {wezelMarkers.length > 0 ? `${wezelMarkers.length} węzłów` : 'Brak węzłów'}
          </span>
        </div>
      )}
      <div style={{ padding: hideHeader ? '0' : '8px', display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minHeight: 0 }}>
        {bezLogowania ? (
          <div style={{ fontSize: '11px', color: 'var(--muted)', padding: '8px' }}>Mapa wymaga zalogowania.</div>
        ) : (<>
        {(loading || apiKeyLoading || mapIdLoading) && (
          <div style={{ fontSize: '11px', color: 'var(--muted)' }}>Ładowanie konfiguracji mapy...</div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && error && (
          <div style={{ fontSize: '11px', color: 'var(--error)' }}>{error}</div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && !error && (!apiKey || !apiKey.trim()) && (
          <div style={{ fontSize: '11px', color: 'var(--error)', padding: '8px' }}>
            Błąd: Brak GMAPS_API_KEY – nie można załadować Map Google
          </div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && !error && apiKey && apiKey.trim() && (!mapId || !mapId.trim()) && (
          <div style={{ fontSize: '11px', color: 'var(--error)', padding: '8px' }}>
            Błąd: Brak Map ID – wymagane dla AdvancedMarkerElement
          </div>
        )}
        {!loading && !apiKeyLoading && !mapIdLoading && !error && apiKey && apiKey.trim() && mapId && mapId.trim() && (
          <div style={{ flex: 1, minHeight: 0, position: 'relative', height: hideHeader ? '160px' : '200px' }}>
            <MapCanvas
              apiKey={apiKey}
              mapId={mapId}
              coords={coords}
              markers={wezelMarkers.map(m => ({ lat: m.lat, lon: m.lon }))}
              onLoad={(map) => {
                mapRef.current = map;
                setMapReadyTick(t => t + 1);
              }}
              fitBoundsFn={fitBounds}
              onReady={notifyReady}
            />
            {(externalLoading || loading) && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.65)',
                  fontSize: '11px',
                  color: 'var(--muted)',
                  pointerEvents: 'none'
                }}
              >
                Ładowanie danych...
              </div>
            )}
            {zamowienia && (
              <div
                style={{
                  position: 'absolute',
                  left: 6,
                  top: 6,
                  zIndex: 1,
                  display: 'flex',
                  gap: 5,
                  alignItems: 'center',
                  background: 'rgba(255,255,255,0.92)',
                  borderRadius: 10,
                  padding: '3px 6px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                }}
              >
                {KROPKI_STANOW.map(({ stan, etykieta }) => {
                  const n = dzien.liczbyStanow[stan];
                  return (
                    <button
                      key={stan}
                      type="button"
                      aria-label={etykieta}
                      aria-pressed={filtr[stan]}
                      title={`${etykieta}: ${n} ${odmiana(n, 'zamówienie', 'zamówienia', 'zamówień')}. Kliknij, żeby ${filtr[stan] ? 'ukryć' : 'pokazać'}`}
                      onClick={() => przelacz(stan)}
                      style={stylKropki(kolorStanu(stan), filtr[stan], false)}
                    />
                  );
                })}
                <span style={{ width: 1, height: 12, background: 'var(--border)' }} />
                <button
                  type="button"
                  aria-label="Węzły"
                  aria-pressed={filtr.Wezly}
                  title={`Węzły: ${wezelMarkers.length}. Kliknij, żeby ${filtr.Wezly ? 'ukryć' : 'pokazać'}`}
                  onClick={() => przelacz('Wezly')}
                  style={stylKropki(KOLOR_WEZLOW, filtr.Wezly, true)}
                />
                {dzien.bezLokalizacji.length > 0 && (
                  <button
                    type="button"
                    aria-label={`${dzien.bezLokalizacji.length} ${odmiana(dzien.bezLokalizacji.length, 'budowa', 'budowy', 'budów')} bez lokalizacji`}
                    aria-expanded={pokazBezLokalizacji}
                    title="Budowy bez lokalizacji na mapie. Kliknij, żeby zobaczyć które"
                    onClick={() => setPokazBezLokalizacji((v) => !v)}
                    style={{ border: 'none', cursor: 'pointer', font: '700 9px/12px sans-serif', color: '#fff', background: '#ef6c00', borderRadius: 6, padding: '0 4px' }}
                  >
                    !{dzien.bezLokalizacji.length}
                  </button>
                )}
              </div>
            )}
            {zamowienia && pokazBezLokalizacji && dzien.bezLokalizacji.length > 0 && (
              <div
                style={{
                  position: 'absolute',
                  left: 6,
                  top: 28,
                  zIndex: 1,
                  maxWidth: 'calc(100% - 12px)',
                  maxHeight: 110,
                  overflowY: 'auto',
                  background: 'var(--card)',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  padding: '4px 8px',
                  fontSize: 11,
                  color: 'var(--text)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.25)'
                }}
              >
                {dzien.bezLokalizacji.map((b) => (
                  <div key={b.nazwa}>{b.nazwa}{b.liczba > 1 ? ` (${b.liczba})` : ''}</div>
                ))}
              </div>
            )}
          </div>
        )}
        </>)}
      </div>
    </div>
  );
};

// Kazde uzycie karty idzie przez granice bledu: awaria map Google nie moze zablokowac strony.
const MapWidgetBezpieczny: React.FC<MapWidgetProps> = (props) => (
  <GranicaBleduMapy onBlad={props.onReady}>
    <MapWidget {...props} />
  </GranicaBleduMapy>
);

export default MapWidgetBezpieczny;

